import { useEffect, useState } from "react";
import { FileText, CheckCircle2 } from "lucide-react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useMe } from "../context/MeContext.js";
import { useToast } from "../context/ToastContext.js";
import { HandbookViewerModal } from "./HandbookViewerModal.js";
import { AsyncButton } from "./ui/async-button.js";

// In-app handbook signing entry point, shown on every authenticated page.
//
// The onboarding wizard's own handbook step is only reachable while onboarding
// is IN_PROGRESS, and the emailed token link is undeliverable while SES is
// unconfigured — so without this an employee who was never routed through the
// wizard (or who finished it before a handbook republish) has no way to sign.
// Signing goes through the same self-only acknowledge endpoint the wizard
// uses, which routes into finalizeHandbookSignature: stamped PDF copy,
// handbookOpenedAt/handbookAckedAt, and the checklist item.

interface HandbookStatus {
  required: boolean;
  signed: boolean;
  handbook: { title: string; version: string; readUrl: string } | null;
}

export function HandbookSignBanner() {
  const me = useMe();
  const toast = useToast();

  const [status, setStatus] = useState<HandbookStatus | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<HandbookStatus>("/me/handbook-status")
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch((err) => { logError("HandbookSignBanner.load", err); });
    return () => { cancelled = true; };
  }, []);

  function openHandbook() {
    setShowModal(true);
    // Mirrors the wizard's viewHandbook(): record that they actually opened it.
    // finalizeHandbookSignature backfills this too, but recording it here keeps
    // the "opened" timestamp truthful rather than equal to the signing time.
    api.post(`/employees/${me.employeeId}/handbook/acknowledge`, { opened: true })
      .catch((err) => { logError("HandbookSignBanner.markOpened", err); });
  }

  async function submit() {
    const name = signedName.trim();
    if (!name) throw new Error("validation");
    setSubmitting(true);
    try {
      await api.post(`/employees/${me.employeeId}/handbook/acknowledge`, { signedName: name });
      setShowModal(false);
      setJustSigned(true);
      toast.show("Handbook signed — thank you!", "success");
    } catch (err) {
      logError("HandbookSignBanner.submit", err);
      toast.show(errMsg(err) ?? "Failed to submit signature", "error");
      throw err;
    } finally { setSubmitting(false); }
  }

  if (justSigned) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 20px", background: "var(--color-success-bg)", borderBottom: "1px solid var(--color-success-border)",
        color: "var(--color-success-fg)", fontSize: 13,
      }}>
        <CheckCircle2 size={16} />
        <span>Handbook signed. Thank you — HR has been notified.</span>
      </div>
    );
  }

  // useMe() returns a blank-employeeId placeholder until /me/profile resolves —
  // holding the banner back until it lands keeps the acknowledge POST from
  // being built against an empty id.
  if (!me.employeeId || !status?.required || !status.handbook) return null;
  const { title, version, readUrl } = status.handbook;

  return (
    <>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "10px 20px", background: "var(--color-warning-bg)", borderBottom: "1px solid var(--color-warning-border)",
        color: "var(--color-warning-fg)", fontSize: 13,
      }}>
        <FileText size={16} />
        <span style={{ flex: 1, minWidth: 220 }}>
          <strong>Action required:</strong> please review and sign the {title} v{version} employee handbook.
        </span>
        <button
          onClick={openHandbook}
          style={{
            padding: "6px 14px", background: "var(--color-warning-fg)", color: "#fff", border: "none",
            borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 12,
          }}>
          Review &amp; Sign
        </button>
      </div>

      {showModal && (
        <>
          <HandbookViewerModal
            url={readUrl}
            title={title}
            version={version}
            onReachedEnd={() => setReachedEnd(true)}
            onClose={() => setShowModal(false)}
          />
          <div style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 10000,
            background: "var(--color-surface-0)", borderTop: "1px solid var(--color-surface-200)",
            padding: "12px 20px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
          }}>
            {!reachedEnd ? (
              <span style={{ fontSize: 13, color: "var(--color-surface-500)" }}>
                Scroll to the end of the handbook to enable signing.
              </span>
            ) : (
              <>
                <label style={{ fontSize: 13, fontWeight: 500 }} htmlFor="handbook-signed-name">
                  Type your full name to sign
                </label>
                <input
                  id="handbook-signed-name"
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  placeholder="Your full name"
                  style={{
                    padding: "7px 10px", border: "1px solid var(--color-surface-300)",
                    borderRadius: "var(--radius-md)", fontSize: 13, minWidth: 220,
                  }}
                />
                <AsyncButton
                  size="sm"
                  onClick={() => submit()}
                  disabled={submitting || !signedName.trim()}
                >
                  Sign Handbook
                </AsyncButton>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
